import React, { useState } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CampaignIcon from "@mui/icons-material/Campaign";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SearchIcon from "@mui/icons-material/Search";
import ShareIcon from "@mui/icons-material/Share";

import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

// ✅ Digital Marketing Content Data
const digitalMarketingContent = [
  {
    section: "SEO & SEM",
    icon: <SearchIcon />,
    items: [
      {
        name: "Search Engine Optimization (SEO)",
        description: "Improve visibility and organic search rankings.",
        fullContent: [
          {
            heading: "Technical SEO",
            text: "Optimizing website infrastructure (speed, mobile-friendliness, indexing) to help search engines crawl and index your site effectively.",
          },
          {
            heading: "On-Page & Off-Page",
            text: "Enhancing content, keywords, and meta tags while building authority through backlinks and social signals.",
          }
        ],
      },
      {
        name: "Search Engine Marketing (SEM)",
        description: "Targeted advertising for instant visibility.",
        fullContent: [
          {
            heading: "PPC Campaigns",
            text: "Pay-Per-Click campaigns (Google Ads) ensure you appear at the top of search results for high-intent keywords.",
          }
        ],
      }
    ],
  },
  {
    section: "Social Media",
    icon: <ShareIcon />,
    items: [
      {
        name: "Social Media Marketing (SMM)",
        description: "Engage audiences on Facebook, Instagram, LinkedIn.",
        fullContent: [
          {
            heading: "Brand Awareness",
            text: "Building a consistent brand voice and visual identity across social platforms to grow followers.",
          },
          {
            heading: "Content Strategy",
            text: "Creating viral content, reels, and stories that drive engagement and convert followers into customers.",
          }
        ],
      },
      {
        name: "Influencer Marketing",
        description: "Leverage key opinion leaders to promote products.",
        fullContent: [
          {
            heading: "Collaboration",
            text: "Partnering with influencers in your niche to reach authentic and engaged audiences.",
          }
        ],
      }
    ],
  },
  {
    section: "Content & Strategy",
    icon: <CampaignIcon />,
    items: [
      {
        name: "Content Marketing",
        description: "Valuable content to attract and retain customers.",
        fullContent: [
          {
            heading: "Blogging & Articles",
            text: "Publishing informative articles that solve user problems and establish authority in your industry.",
          }
        ],
      },
      {
        name: "Email Marketing",
        description: "Direct communication to nurture leads.",
        fullContent: [
          {
            heading: "Automated Workflows",
            text: "Sending personalized email sequences based on user behavior to drive conversions.",
          }
        ],
      }
    ],
  },
  {
    section: "Analytics & Growth",
    icon: <TrendingUpIcon />,
    items: [
      {
        name: "Performance Analytics",
        description: "Measure ROI and optimize campaigns.",
        fullContent: [
          {
            heading: "Data-Driven Decisions",
            text: "Using tools like Google Analytics to track user behavior, conversion rates, and campaign effectiveness.",
          }
        ],
      },
      {
        name: "Conversion Rate Optimization (CRO)",
        description: "Turn more visitors into customers.",
        fullContent: [
          {
            heading: "A/B Testing",
            text: "Testing different headlines, images, and CTAs to maximize the percentage of converting users.",
          }
        ],
      }
    ],
  }
];

function DigitalMarketing() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box sx={{ bgcolor: "#f4f6f8", minHeight: "100vh", pt: 12, pb: 8 }}>
      <Container maxWidth="xl">
        {/* Header Navigation */}
        <Box mb={4} display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              fontWeight: 700,
              color: "#034522",
              mr: 2,
              textTransform: "none",
              borderRadius: "50px",
              px: 3,
              "&:hover": { bgcolor: "rgba(3, 69, 34, 0.1)" },
            }}
          >
            Back to Services
          </Button>
          <Typography variant="h4" fontWeight={800} color="#1a1a1a" sx={{ fontSize: { xs: "1.5rem", md: "2.125rem" } }}>
            Digital Marketing
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Sidebar Tabs (Desktop) / Top Tabs (Mobile) */}
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                background: "#fff",
              }}
            >
              <Tabs
                orientation={isMobile ? "horizontal" : "vertical"}
                variant="scrollable"
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  borderRight: { md: 1, xs: 0 },
                  borderColor: "divider",
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#1A9E7F",
                    width: "4px",
                    left: { md: 0 },
                    right: "auto"
                  },
                }}
              >
                {digitalMarketingContent.map((section, index) => (
                  <Tab
                    key={index}
                    icon={<Box sx={{ color: activeTab === index ? "#1A9E7F" : "inherit" }}>{section.icon}</Box>}
                    iconPosition="start"
                    label={section.section}
                    sx={{
                      alignItems: "center",
                      justifyContent: "flex-start",
                      textTransform: "none",
                      fontWeight: 600,
                      color: activeTab === index ? "#034522" : "#666",
                      fontSize: "1rem",
                      py: 3,
                      px: 3,
                      minHeight: 64,
                      transition: "all 0.3s",
                      "&.Mui-selected": {
                        color: "#034522",
                        bgcolor: "rgba(26, 158, 127, 0.08)",
                      },
                      "&:hover": {
                        bgcolor: "rgba(0,0,0,0.02)",
                      }
                    }}
                  />
                ))}
              </Tabs>
            </Paper>
          </Grid>

          {/* Main Content Area */}
          <Grid item xs={12} md={9}>
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 4,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                  minHeight: "60vh",
                  bgcolor: "#fff"
                }}
              >
                <Box mb={4} display="flex" alignItems="center">
                    <Box 
                        sx={{ 
                            p: 1.5, 
                            borderRadius: '12px', 
                            bgcolor: 'rgba(3, 69, 34, 0.1)', 
                            color: '#034522',
                            mr: 2,
                            display: 'flex',
                            boxShadow: '0 4px 12px rgba(3, 69, 34, 0.1)'
                        }}
                    >
                        {digitalMarketingContent[activeTab].icon}
                    </Box>
                    <Typography variant="h5" fontWeight={800} color="#034522">
                    {digitalMarketingContent[activeTab].section}
                    </Typography>
                </Box>
                
                <Box>
                  {digitalMarketingContent[activeTab].items.map((item, i) => (
                    <Accordion
                      key={i}
                      disableGutters
                      elevation={0}
                      sx={{
                        mb: 2,
                        borderRadius: "16px !important",
                        border: "1px solid #edf2f7",
                        "&:before": { display: "none" },
                        "&:hover": { bgcolor: "#f8f9fa", borderColor: "#e2e8f0" },
                        transition: "all 0.2s"
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: "#1A9E7F" }} />}
                        sx={{ px: 3, py: 1 }}
                      >
                        <Box>
                          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', color: '#1a1a1a' }}>
                            {item.name}
                          </Typography>
                          <Typography variant="body2" color="#666" sx={{ mt: 0.5 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 3, pb: 4, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                        {item.fullContent.map((content, idx) => (
                          <Box key={idx} mt={3}>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              gutterBottom
                              color="#1A9E7F"
                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1A9E7F' }} />
                                {content.heading}
                            </Typography>
                            {content.text && (
                              <Typography paragraph variant="body2" color="#4a5568" mb={2} sx={{ lineHeight: 1.7 }}>
                                {content.text}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default DigitalMarketing;
